/**
 * Review meeting + Strategy module contracts (prototype v10.346 tabs "Review" and "Strategy").
 * Shapes and fixed catalogues only; every number is computed on the server (apps/api), never here
 * and never by the model.
 */
import { z } from 'zod';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const uuid = z.string().uuid();
const inr = z.number().int().min(0).max(10_000_000_000);

// ─────────────────────────── Review meeting ───────────────────────────

export const ReviewOfficerId = z.enum(['customer', 'sales', 'revenue', 'marketing', 'strategy']);
export type ReviewOfficerId = z.infer<typeof ReviewOfficerId>;

export interface ReviewOfficerDef {
  id: ReviewOfficerId;
  /** Plain name shown on screen. */
  label: string;
  /** Prototype role code, kept for traceability (tooltip only). */
  code: 'CCRO' | 'CSO' | 'CRO' | 'CMO' | 'CSO2';
  mandate: string;
  /** Needs a strategy plan on file before it can answer (prototype reviewAdequacy). */
  needsPlan: boolean;
  questions: readonly string[];
}

/** Preset questions ported from _reviewRoles (v10.346), de-personalised. */
export const REVIEW_OFFICERS: readonly ReviewOfficerDef[] = [
  {
    id: 'customer', code: 'CCRO', label: 'Customer relationships advisor', needsPlan: false,
    mandate: 'Every relationship, lead to repeat customer: trust, temperature, timing, and the next step the selling rules allow.',
    questions: [
      'Which 5 relationships warmed and which 5 cooled this month?',
      'Which relationship did we damage by moving early or pitching?',
      'Which person named a date and passed it, and is each one still deciding or a soft no?',
      'Which paying customer had no value moment this month?',
      'Which paying customer has no satisfaction check this quarter?',
      'Where do we know only one person at a company, and what did we do to add a second name?',
      'Which 3 people know us well enough to refer us, and did we ask?',
      'Which judgement about a person is older than 14 days, and which of your judgements turned out wrong?',
      'What did you stop doing, and what do you need from me next month?',
    ],
  },
  {
    id: 'sales', code: 'CSO', label: 'Sales advisor', needsPlan: true,
    mandate: 'Stage movement, conversion discipline, pipeline build, selling capacity.',
    questions: [
      'Stage by stage: how many people moved forward, back, or stalled over 60 days?',
      'For each open opportunity, what is the one thing that must be true to close it?',
      'Which prospect would sign this quarter if we did one specific thing?',
      'How many first, second and third messages went out, and did any third message go without a genuine reason?',
      'Conversion at each step against the plan: where is the biggest drop?',
      'Selling hours this month against the weekly limit: which person took hours they did not deserve?',
      'How many warm conversations began this month that will be pipeline in 90 days?',
      'Which deal did you forecast wrong last month, and what did the miss teach you?',
      'What will you stop doing, and what do you need from me?',
    ],
  },
  {
    id: 'revenue', code: 'CRO', label: 'Revenue advisor', needsPlan: true,
    mandate: 'Paid yearly revenue, forecast, price tiers, revenue sources, the honest floor.',
    questions: [
      'Paid yearly revenue today, weighted pipeline, realistic by the deadline, then the gap to target.',
      'If nothing changes from today, what is the yearly revenue on the deadline? Say it plainly.',
      'Which one account or deal changes the quarter, and what is its honest chance?',
      'Renewal versus new customer versus upgrade this month: repeatable or one-off?',
      'Which price tier is underperforming: a pricing problem, a proof problem, or a capacity problem?',
      'Second seller, channel partner, multi-team deal: what became true, and what must become true in 30 days?',
      'Which revenue source rests on one person, and what revenue is at risk?',
      'Does cash cover the next 90 days at current spend: yes, borderline, or no?',
      'What did the plan get wrong last month, and which assumption would you now cut?',
    ],
  },
  {
    id: 'marketing', code: 'CMO', label: 'Marketing advisor', needsPlan: true,
    mandate: 'Demand, proof, positioning, content, and the material selling needs.',
    questions: [
      'Which 3 demand drivers and 3 competitor moves shaped the market this month, and what did we change in our message?',
      'What proof did we add this month: a case study, a buyer outcome, a reference, or a number a prospect can repeat?',
      'How many inbound conversations did content create, and which piece on which channel produced them?',
      'Which material did selling ask for and not get, and what did it cost in the pipeline?',
      'In one sentence, what does a typical buyer believe we do, and is that the sentence we want?',
      'Which content piece could be read as a sales pitch, and what did we learn?',
      'Where is our shareable material still empty for a live relationship, and what goes in by the next review?',
      'What did you stop doing, and what do you need from me?',
    ],
  },
  {
    id: 'strategy', code: 'CSO2', label: 'Strategy advisor', needsPlan: false,
    mandate: 'Reads what the other four advisors found this sitting, names the real problems for the business, and puts three solutions against each with one recommendation.',
    questions: [
      'Sum up this sitting: what are the three real problems for the business, with three solutions each and your recommendation?',
      'Where is the one constraint holding revenue back right now, and what relieves it?',
      'Which single decision would change the next 90 days most?',
      'Pre-mortem: it is the deadline and we missed the target. What killed it, in order of likelihood?',
      'Which assumption in the plan is doing the most work and is least tested?',
      'Inversion: what would guarantee failure this quarter, and which of those are we doing?',
      'Stop, start, continue for the next 30 days.',
      'What is our honest position in the market, and where is the wedge?',
    ],
  },
] as const;

/** "Facts the system cannot see on its own" (prototype _bizFactFields), seven fields. */
export const REVIEW_FACT_FIELDS = [
  { key: 'team', label: 'Team and roles', placeholder: 'e.g. The owner is the only seller; one person runs engineering' },
  { key: 'cash90', label: 'Does cash cover the next 90 days at current spend?', placeholder: 'yes / borderline / no, and as of which date' },
  { key: 'burn', label: 'Monthly spend (₹)', placeholder: 'e.g. 1,20,000' },
  { key: 'hoursMonth', label: 'Selling hours this month so far', placeholder: 'e.g. 42' },
  { key: 'liPosts', label: 'LinkedIn posts published this month', placeholder: 'e.g. 7' },
  { key: 'inboundSources', label: 'Inbound this month by source', placeholder: 'e.g. 1 website form, 1 LinkedIn, 5 personal network' },
  { key: 'positioning', label: 'Current one-sentence positioning', placeholder: 'What a buyer should believe we do' },
] as const;
export type ReviewFactKey = (typeof REVIEW_FACT_FIELDS)[number]['key'];

export const ReviewFactLogType = z.enum(['market', 'message', 'asset', 'forecast', 'owner']);
export const REVIEW_FACT_LOG_TYPES: readonly { key: z.infer<typeof ReviewFactLogType>; label: string }[] = [
  { key: 'market', label: 'Market or competitor event' },
  { key: 'message', label: 'Message or positioning change' },
  { key: 'asset', label: 'Material request (who asked, what, delivered?)' },
  { key: 'forecast', label: 'Your revenue estimate or forecast note' },
  { key: 'owner', label: 'Other fact' },
];

const factText = z.string().trim().max(400);
export const ReviewFacts = z.object({
  team: factText.default(''), cash90: factText.default(''), burn: factText.default(''), hoursMonth: factText.default(''),
  liPosts: factText.default(''), inboundSources: factText.default(''), positioning: factText.default(''),
});
export type ReviewFacts = z.infer<typeof ReviewFacts>;

export const RsVerticalQuery = z.object({ verticalId: uuid });

export const ReviewFactLogEntry = z.object({ id: uuid, date: isoDate, type: ReviewFactLogType, text: z.string(), authorName: z.string() });
export type ReviewFactLogEntry = z.infer<typeof ReviewFactLogEntry>;

export const ReviewOfficerGate = z.object({
  ok: z.boolean(),
  /** A soft gate still answers, with the warning shown above the answer. */
  soft: z.boolean(),
  message: z.string().nullable(),
  fix: z.enum(['strategy']).nullable(),
});

export const ReviewNeedItem = z.object({ q: z.string().min(1).max(300), options: z.array(z.string().min(1).max(120)).max(5) });
export type ReviewNeedItem = z.infer<typeof ReviewNeedItem>;

export const ReviewRowLink = z.object({ relationshipId: uuid, name: z.string(), nextMove: z.string() });
export type ReviewRowLink = z.infer<typeof ReviewRowLink>;

export const ReviewAnswer = z.object({
  id: uuid,
  officer: ReviewOfficerId,
  question: z.string(),
  /** answered | need (advisor asks you a fact) | unavailable (AI returned nothing) | blocked (gate). */
  status: z.enum(['answered', 'need', 'unavailable', 'blocked']),
  verdict: z.string().nullable(),
  body: z.string().nullable(),
  /** The one recommended action; for a person it is the server-computed next step, word for word. */
  action: z.object({ relationshipId: uuid.nullable(), text: z.string() }).nullable(),
  need: z.array(ReviewNeedItem).nullable(),
  rows: z.array(ReviewRowLink),
  softNote: z.string().nullable(),
  blockedReason: z.string().nullable(),
  facts: z.string().nullable(),
  followUps: z.array(z.object({ question: z.string(), answer: z.string().nullable(), at: z.string() })),
  logged: z.boolean(),
  loggedOn: isoDate.nullable(),
  sittingDate: isoDate,
  createdAt: z.string(),
  authorName: z.string(),
});
export type ReviewAnswer = z.infer<typeof ReviewAnswer>;

export const ReviewOverview = z.object({
  verticalId: uuid,
  today: isoDate,
  monthStart: isoDate,
  plan: z.object({ id: uuid, version: z.number().int(), date: isoDate, ageDays: z.number().int() }).nullable(),
  planStale: z.boolean(),
  recordThisMonth: z.number().int(),
  recordTotal: z.number().int(),
  facts: ReviewFacts,
  factsUpdatedOn: isoDate.nullable(),
  /** Keys of facts the revenue and sales advisors need that are still empty. */
  factsMissing: z.array(z.string()),
  factLog: z.array(ReviewFactLogEntry),
  officers: z.array(z.object({ id: ReviewOfficerId, gate: ReviewOfficerGate })),
  sitting: z.array(ReviewAnswer),
});
export type ReviewOverview = z.infer<typeof ReviewOverview>;

export const ReviewSaveFactsRequest = z.object({ verticalId: uuid, facts: ReviewFacts });
export const ReviewAddFactLogRequest = z.object({ verticalId: uuid, type: ReviewFactLogType, text: z.string().trim().min(1).max(400) });
export const ReviewAskRequest = z.object({ verticalId: uuid, officer: ReviewOfficerId, question: z.string().trim().min(3).max(600) });
export const ReviewContinueRequest = z.object({
  answers: z.array(z.object({ q: z.string().max(300), picked: z.array(z.string().max(120)).max(5), text: z.string().max(300) })).min(1).max(3),
});
export const ReviewFollowUpRequest = z.object({ question: z.string().trim().min(3).max(400) });
export const ReviewAnswerListQuery = z.object({ verticalId: uuid, scope: z.enum(['sitting', 'record']).default('record'), officer: ReviewOfficerId.optional() });
export const ReviewAnswerList = z.object({ items: z.array(ReviewAnswer) });
export const ReviewPushResponse = z.object({ pushed: z.array(z.object({ relationshipId: uuid, name: z.string() })), text: z.string() });
export const ReviewAnswerDataResponse = z.object({ id: uuid, text: z.string() });
export const ReviewOfficerDataResponse = z.object({ officer: ReviewOfficerId, text: z.string(), rowCount: z.number().int(), generatedAt: z.string() });

// ─────────────────────────── Strategy ───────────────────────────

export const StrategySource = z.object({
  key: z.string().regex(/^S[1-8]$/),
  name: z.string().trim().min(1).max(80),
  estimateInr: inr.nullable(),
  note: z.string().trim().max(200).default(''),
});
export type StrategySource = z.infer<typeof StrategySource>;

export const StrategyAssumptions = z.object({
  winRatePct: z.number().min(1).max(100),
  avgDealInr: z.number().int().min(1).max(1_000_000_000),
  cycleDays: z.number().int().min(1).max(730),
  capacityHoursPerWeek: z.number().min(0).max(80),
  hoursPerOpportunity: z.number().min(0.5).max(200),
});
export type StrategyAssumptions = z.infer<typeof StrategyAssumptions>;

export const STRATEGY_DEFAULT_ASSUMPTIONS: StrategyAssumptions = { winRatePct: 20, avgDealInr: 300_000, cycleDays: 90, capacityHoursPerWeek: 18, hoursPerOpportunity: 12 };

export const StrategyObjective = z.object({
  id: uuid.nullable(),
  scope: z.enum(['area', 'group']),
  verticalId: uuid.nullable(),
  targetInr: inr,
  deadline: isoDate.nullable(),
  mission: z.string(),
  outsideIn: z.string(),
  sources: z.array(StrategySource),
  assumptions: StrategyAssumptions,
  updatedOn: isoDate.nullable(),
  /** Optimistic concurrency token (updated_at epoch ms); null when never saved. */
  version: z.number().int().nullable(),
});
export type StrategyObjective = z.infer<typeof StrategyObjective>;

export const StrategyObjectiveQuery = z.object({ verticalId: uuid.optional() });
export const StrategySaveObjectiveRequest = z.object({
  verticalId: uuid.nullable(),
  targetInr: inr,
  deadline: isoDate,
  mission: z.string().trim().max(400).default(''),
  outsideIn: z.string().trim().max(1200).default(''),
  sources: z.array(StrategySource).max(8).default([]),
  assumptions: StrategyAssumptions,
  version: z.number().int().nullable(),
});

const num = (min: number, max: number) => z.coerce.number().min(min).max(max);
export const StrategyWhatIfQuery = z.object({
  verticalId: uuid.optional(),
  winRatePct: num(1, 100),
  avgDealInr: num(1, 1_000_000_000),
  cycleDays: num(1, 730),
  capacityHoursPerWeek: num(0, 80),
  hoursPerOpportunity: num(0.5, 200),
  targetInr: num(0, 10_000_000_000).optional(),
  deadline: isoDate.optional(),
});
export const StrategyWhatIfVerdict = z.enum(['on_track', 'tight', 'short', 'no_time', 'no_target']);
export const StrategyWhatIfResult = z.object({
  today: isoDate,
  deadline: isoDate.nullable(),
  daysLeft: z.number().int(),
  targetInr: z.number().int(),
  paidArrInr: z.number().int(),
  payingCount: z.number().int(),
  openPipelineInr: z.number().int(),
  openCount: z.number().int(),
  weightedPipelineInr: z.number().int(),
  gapInr: z.number().int(),
  dealsNeeded: z.number().int(),
  opportunitiesNeeded: z.number().int(),
  opportunitiesPerWeekNeeded: z.number(),
  sellingDaysLeft: z.number().int(),
  hoursNeeded: z.number(),
  hoursAvailable: z.number(),
  capacityFits: z.boolean(),
  opportunitiesCapacity: z.number().int(),
  projectedInr: z.number().int(),
  coveragePct: z.number().int(),
  shortfallInr: z.number().int(),
  verdict: StrategyWhatIfVerdict,
  verdictText: z.string(),
  areas: z.array(z.string()),
});
export type StrategyWhatIfResult = z.infer<typeof StrategyWhatIfResult>;

export const StrategyInitiativeStatus = z.enum(['planned', 'in_progress', 'done', 'dropped']);
export const StrategyInitiative = z.object({
  id: z.string(),
  title: z.string(),
  action: z.string(),
  sourceKey: z.string().nullable(),
  dueDate: isoDate.nullable(),
  hoursPerWeek: z.number(),
  leadingIndicator: z.string(),
  rows: z.array(z.object({ relationshipId: uuid, name: z.string() })),
  status: StrategyInitiativeStatus,
  statusOn: isoDate.nullable(),
  notes: z.string(),
  /** Server-found conflicts, e.g. a person the selling rules hold. Plain words. */
  flags: z.array(z.string()),
  overdue: z.boolean(),
  byHand: z.boolean(),
});
export type StrategyInitiative = z.infer<typeof StrategyInitiative>;

export const StrategyPlanSourceLine = z.object({
  key: z.string(), name: z.string(), estimateInr: z.number().int().nullable(), pipelineInr: z.number().int(), realisticInr: z.number().int(),
  confidence: z.enum(['high', 'medium', 'low']).nullable(), whatMustBeTrue: z.string().nullable(), blocker: z.string().nullable(),
});
export const StrategyPlanNumbers = z.object({
  paidArrInr: z.number().int(), openPipelineInr: z.number().int(), weightedPipelineInr: z.number().int(),
  realisticTotalInr: z.number().int(), gapInr: z.number().int(), coveragePct: z.number().int(),
  sources: z.array(StrategyPlanSourceLine), totalHoursPerWeek: z.number(), capacityHoursPerWeek: z.number(), fitsCapacity: z.boolean(),
  expansionCount: z.number().int(),
});
export const StrategyPlanAi = z.object({
  verdict: z.string(),
  lifecycleRead: z.string(),
  segments: z.array(z.object({ segment: z.string(), decision: z.enum(['pursue', 'ignore']), why: z.string() })),
  stopDoing: z.array(z.object({ activity: z.string(), why: z.string() })),
  killSignals: z.array(z.object({ signal: z.string(), response: z.string() })),
  expansion: z.array(z.object({ relationshipId: uuid, name: z.string(), opportunity: z.string(), unlock: z.string() })),
  northStar: z.object({ metric: z.string(), why: z.string() }),
});
export const StrategyPlan = z.object({
  id: uuid, verticalId: uuid, version: z.number().int(), date: isoDate, reason: z.string(), status: z.enum(['active', 'closed']),
  objective: z.object({ targetInr: z.number().int(), deadline: isoDate }),
  numbers: StrategyPlanNumbers,
  ai: StrategyPlanAi.nullable(),
  aiUnavailable: z.boolean(),
  initiatives: z.array(StrategyInitiative),
  corrections: z.array(z.string()),
  stabilityFlag: z.string().nullable(),
  diff: z.object({ added: z.array(z.string()), removed: z.array(z.string()), realisticFrom: z.number().int(), realisticTo: z.number().int() }).nullable(),
  ageDays: z.number().int(),
  stale: z.boolean(),
  createdAt: z.string(),
  authorName: z.string(),
  /** Optimistic concurrency for initiative edits (updated_at epoch ms). */
  rowVersion: z.number().int(),
});
export type StrategyPlan = z.infer<typeof StrategyPlan>;

export const StrategyPlanSummary = z.object({
  id: uuid, version: z.number().int(), date: isoDate, reason: z.string(), targetInr: z.number().int(),
  realisticTotalInr: z.number().int(), initiativeCount: z.number().int(), status: z.enum(['active', 'closed']), aiUnavailable: z.boolean(),
});
export const StrategyPlanList = z.object({ items: z.array(StrategyPlanSummary) });
export type StrategyPlanList = z.infer<typeof StrategyPlanList>;

export const StrategyRunStatus = z.enum(['queued', 'running', 'done', 'failed']);
export const StrategyRun = z.object({
  id: uuid, verticalId: uuid, status: StrategyRunStatus, progress: z.number().int().min(0).max(100), step: z.string(),
  error: z.string().nullable(), planId: uuid.nullable(), aiUnavailable: z.boolean(), startedAt: z.string(), finishedAt: z.string().nullable(),
});
export type StrategyRun = z.infer<typeof StrategyRun>;
export const StrategyRunRequest = z.object({ verticalId: uuid, fresh: z.boolean().default(false), reason: z.string().trim().max(300).optional() });

export const StrategyUpdateInitiativeRequest = z.object({ status: StrategyInitiativeStatus.optional(), notes: z.string().max(1000).optional(), version: z.number().int() });
export const StrategyAddInitiativeRequest = z.object({
  title: z.string().trim().min(3).max(120), action: z.string().trim().min(3).max(400), sourceKey: z.string().regex(/^S[1-8]$/).nullable().default(null),
  dueDate: isoDate.nullable().default(null), hoursPerWeek: z.number().min(0.5).max(40), leadingIndicator: z.string().trim().max(200).default(''),
  relationshipIds: z.array(uuid).max(10).default([]), version: z.number().int(),
});
export const StrategyPushInitiativeResponse = z.object({ pushed: z.number().int(), skipped: z.number().int(), plan: StrategyPlan });

export const StrategyWeeklyQuestion = z.object({
  id: z.string(), text: z.string(), type: z.enum(['single', 'multi']),
  options: z.array(z.object({ value: z.string(), label: z.string() })), free: z.boolean(),
});
export const StrategyWeeklyState = z.object({
  due: z.boolean(), daysUntilDue: z.number().int(), last: z.object({ id: uuid, date: isoDate, summary: z.string() }).nullable(),
  questions: z.array(StrategyWeeklyQuestion), northStarLog: z.array(z.object({ date: isoDate, assessments: z.string() })), planId: uuid.nullable(),
  history: z.array(z.object({ id: uuid, date: isoDate, summary: z.string() })),
});
export type StrategyWeeklyState = z.infer<typeof StrategyWeeklyState>;
export const StrategySubmitWeeklyRequest = z.object({
  verticalId: uuid,
  answers: z.record(z.string().max(40), z.object({ picked: z.array(z.string().max(80)).max(12), text: z.string().max(300).default('') })),
});
export const StrategySubmitWeeklyResponse = z.object({ review: z.object({ id: uuid, date: isoDate, summary: z.string() }), run: StrategyRun });

export const StrategyTargetStatus = z.enum(['unknown', 'known', 'approached', 'in_pipeline']);
export const StrategyTargetCompany = z.object({ id: uuid, company: z.string(), headName: z.string(), role: z.string(), status: StrategyTargetStatus, note: z.string(), updatedOn: isoDate });
export type StrategyTargetCompany = z.infer<typeof StrategyTargetCompany>;
/** Link a person to a revenue source (replaces the prototype's hard-coded STRAT_ACCOUNT_MAP). */
export const StrategySourceLinkRequest = z.object({ relationshipId: uuid, sourceKey: z.string().regex(/^S[1-8]$/).nullable() });
export const StrategyPeopleList = z.object({ items: z.array(z.object({
  relationshipId: uuid, name: z.string(), company: z.string(), stage: z.string(), section: z.string(), paying: z.boolean(), open: z.boolean(),
  dealValueInr: z.number().int(), sourceKey: z.string().nullable(), nextStep: z.string(), onHold: z.boolean(),
})) });
export type StrategyPeopleList = z.infer<typeof StrategyPeopleList>;
export const StrategyTargetList = z.object({ items: z.array(StrategyTargetCompany) });
export const StrategyAddTargetsRequest = z.object({ verticalId: uuid, paste: z.string().trim().min(2).max(20_000) });
export const StrategyAddTargetsResponse = z.object({ added: z.number().int(), skipped: z.array(z.string()) });
export const StrategyUpdateTargetRequest = z.object({ status: StrategyTargetStatus.optional(), note: z.string().max(400).optional() });

export const StrategyOverview = z.object({
  verticalId: uuid,
  today: isoDate,
  objective: StrategyObjective,
  groupObjective: StrategyObjective,
  plan: StrategyPlan.nullable(),
  run: StrategyRun.nullable(),
  weekly: StrategyWeeklyState,
  drift: z.object({
    planAgeDays: z.number().int().nullable(), stale: z.boolean(), overdueInitiatives: z.number().int(),
    weeklyDue: z.boolean(), stabilityFlag: z.string().nullable(),
  }),
  targetCount: z.number().int(),
  targetKnownCount: z.number().int(),
});
export type StrategyOverview = z.infer<typeof StrategyOverview>;

/** Route table for this module (merge into routes.ts on registration). */
export const reviewStrategyRoutes = {
  reviewOverview: { method: 'GET', path: '/review/overview' },
  reviewSaveFacts: { method: 'PUT', path: '/review/facts' },
  reviewAddFactLog: { method: 'POST', path: '/review/fact-log' },
  reviewDeleteFactLog: { method: 'DELETE', path: '/review/fact-log/:id' },
  reviewOfficerData: { method: 'GET', path: '/review/officers/:officer/data' },
  reviewAsk: { method: 'POST', path: '/review/ask' },
  reviewAnswers: { method: 'GET', path: '/review/answers' },
  reviewAnswerData: { method: 'GET', path: '/review/answers/:id/data' },
  reviewContinue: { method: 'POST', path: '/review/answers/:id/continue' },
  reviewFollowUp: { method: 'POST', path: '/review/answers/:id/follow-up' },
  reviewLogAnswer: { method: 'POST', path: '/review/answers/:id/log' },
  reviewPushAnswer: { method: 'POST', path: '/review/answers/:id/push' },
  reviewDismissAnswer: { method: 'POST', path: '/review/answers/:id/dismiss' },
  reviewClearOfficer: { method: 'POST', path: '/review/officers/:officer/clear' },
  reviewExport: { method: 'GET', path: '/review/record/export' },
  strategyOverview: { method: 'GET', path: '/strategy/overview' },
  strategyObjective: { method: 'GET', path: '/strategy/objective' },
  strategySaveObjective: { method: 'PUT', path: '/strategy/objective' },
  strategyWhatIf: { method: 'GET', path: '/strategy/what-if' },
  strategyPeople: { method: 'GET', path: '/strategy/people' },
  strategyLinkSource: { method: 'PUT', path: '/strategy/source-links' },
  strategyStartRun: { method: 'POST', path: '/strategy/runs' },
  strategyRun: { method: 'GET', path: '/strategy/runs/:id' },
  strategyPlans: { method: 'GET', path: '/strategy/plans' },
  strategyPlan: { method: 'GET', path: '/strategy/plans/:id' },
  strategyPlanExport: { method: 'GET', path: '/strategy/plans/:id/export' },
  strategyUpdateInitiative: { method: 'PATCH', path: '/strategy/plans/:id/initiatives/:iid' },
  strategyAddInitiative: { method: 'POST', path: '/strategy/plans/:id/initiatives' },
  strategyPushInitiative: { method: 'POST', path: '/strategy/plans/:id/initiatives/:iid/push' },
  strategyWeekly: { method: 'GET', path: '/strategy/weekly' },
  strategySubmitWeekly: { method: 'POST', path: '/strategy/weekly' },
  strategyTargets: { method: 'GET', path: '/strategy/targets' },
  strategyAddTargets: { method: 'POST', path: '/strategy/targets' },
  strategyUpdateTarget: { method: 'PATCH', path: '/strategy/targets/:id' },
  strategyDeleteTarget: { method: 'DELETE', path: '/strategy/targets/:id' },
} as const;
